/**
 * @file SidebarNav.tsx
 * @description Sidebar navigation with collapsible groups, active-state highlighting,
 *              tooltip labels in collapsed mode, and secure PIN gate protection.
 *              NAV_ITEMS are imported from the shared nav-items.ts — edit there to
 *              update both the sidebar AND the header search palette at once.
 */

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';
import { NAV_ITEMS, type NavItem } from './nav-items';
import useUIStore from '@/store/uiStore';
import { PinVerificationModal } from '@/components/common/PinVerificationModal';

interface SidebarNavProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

const hasActiveDescendant = (item: NavItem, currentPath: string): boolean => {
  if (item.path && (item.path === currentPath || currentPath.startsWith(item.path + '/'))) return true;
  if (item.children) {
    return item.children.some((child) => hasActiveDescendant(child, currentPath));
  }
  return false;
};

const getLeafDescendants = (item: NavItem, parentIsPrivate = false): NavItem[] => {
  const leaves: NavItem[] = [];
  const recurse = (node: NavItem, inheritedPrivate: boolean) => {
    const isPrivate = Boolean(node.isPrivate || inheritedPrivate);
    if (node.path) {
      leaves.push({ ...node, isPrivate });
    } else if (node.children) {
      node.children.forEach((child) => recurse(child, isPrivate));
    }
  };
  const isPrivate = Boolean(item.isPrivate || parentIsPrivate);
  item.children?.forEach((child) => recurse(child, isPrivate));
  return leaves;
};

const SidebarNav = ({ collapsed, onNavigate }: SidebarNavProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const { unlockedPaths, unlockPath } = useUIStore();

  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<{ path: string; label: string } | null>(null);

  // Auto-open the group containing the active route
  useEffect(() => {
    const checkAndOpenGroups = (items: NavItem[]) => {
      items.forEach((item) => {
        if (item.children && item.groupKey) {
          const hasActive = hasActiveDescendant(item, pathname);
          if (hasActive) {
            setOpenGroups((prev) =>
              prev.includes(item.groupKey!) ? prev : [...prev, item.groupKey!],
            );
            checkAndOpenGroups(item.children);
          }
        }
      });
    };
    checkAndOpenGroups(NAV_ITEMS);
  }, [pathname]);

  const toggleGroup = (groupKey: string) => {
    setOpenGroups((prev) =>
      prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey],
    );
  };

  const handlePinSuccess = () => {
    if (pendingNav) {
      unlockPath(pendingNav.path);
      setIsPinModalOpen(false);
      navigate(pendingNav.path);
      onNavigate?.();
      setPendingNav(null);
    }
  };

  const renderItem = (item: NavItem, depth = 0, parentIsPrivate = false): React.ReactNode => {
    const Icon = item.icon;
    const effectiveIsPrivate = Boolean(item.isPrivate || parentIsPrivate);

    // External link — opens the target app in a new tab
    if (item.externalUrl) {
      const externalLink = (
        <a
          key={item.externalUrl}
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onNavigate?.()}
          className={cn(
            'flex items-center gap-3 rounded-xl px-3 h-[44px] text-[14px] font-medium transition-all duration-150 group',
            'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
            collapsed && 'justify-center px-0 border-l-0',
            depth > 0 && 'h-[36px] text-[13px] px-2.5',
          )}
        >
          <span className={cn('shrink-0', collapsed ? 'w-10 flex justify-center' : 'w-5')}>
            <Icon
              size={depth > 0 ? 16 : 20}
              className="text-sidebar-muted transition-colors duration-150 group-hover:text-sidebar-foreground"
            />
          </span>
          {!collapsed && (
            <>
              <span className="flex-1 truncate">{item.label}</span>
              <ExternalLink size={14} className="shrink-0 text-sidebar-muted group-hover:text-sidebar-foreground" />
            </>
          )}
        </a>
      );

      if (collapsed && depth === 0) {
        return (
          <Tooltip key={item.externalUrl} delayDuration={0}>
            <TooltipTrigger asChild>{externalLink}</TooltipTrigger>
            <TooltipContent side="right" className="font-medium z-[9999]">
              {item.label}
            </TooltipContent>
          </Tooltip>
        );
      }

      return externalLink;
    }

    // Leaf nav item (direct link)
    if (item.path) {
      const isLocked = effectiveIsPrivate && !unlockedPaths[item.path];
      
      const handleLinkClick = (e: React.MouseEvent) => {
        if (isLocked) {
          e.preventDefault();
          setPendingNav({ path: item.path!, label: item.label });
          setIsPinModalOpen(true);
        } else {
          onNavigate?.();
        }
      };

      const link = (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={handleLinkClick}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-xl px-3 h-[44px] text-[14px] font-medium transition-all duration-150 group',
              isActive
                ? 'bg-sidebar-primary/20 text-sidebar-primary font-semibold'
                : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
              collapsed && 'justify-center px-0 border-l-0',
              depth > 0 && 'h-[36px] text-[13px] px-2.5',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span className={cn('shrink-0', collapsed ? 'w-10 flex justify-center' : 'w-5')}>
                <Icon
                  size={depth > 0 ? 16 : 20}
                  className={cn(
                    'transition-colors duration-150',
                    isActive
                      ? 'text-sidebar-primary'
                      : 'text-sidebar-muted group-hover:text-sidebar-foreground'
                  )}
                />
              </span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </>
          )}
        </NavLink>
      );

      if (collapsed && depth === 0) {
        return (
          <Tooltip key={item.path} delayDuration={0}>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right" className="font-medium z-[9999]">
              {item.label}
            </TooltipContent>
          </Tooltip>
        );
      }

      return link;
    }

    // Group with children
    const isOpen = item.groupKey ? openGroups.includes(item.groupKey) : false;
    const hasActiveChild = hasActiveDescendant(item, pathname);

    const groupButton = (
      <button
        key={item.groupKey}
        onClick={() => {
          if (!collapsed && item.groupKey) {
            toggleGroup(item.groupKey);
          }
        }}
        className={cn(
          'w-full flex items-center gap-3 rounded-xl px-3 h-[44px] text-[14px] font-medium transition-all duration-150 outline-none border-l-0 border-transparent',
          hasActiveChild
            ? 'text-sidebar-foreground bg-sidebar-accent font-semibold'
            : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
          collapsed && 'justify-center px-0 border-none',
          depth > 0 && 'h-[38px] text-[13px]',
        )}
      >
        <span className={cn('shrink-0', collapsed ? 'w-10 flex justify-center' : 'w-5')}>
          <Icon
            size={depth > 0 ? 16 : 20}
            className={cn(
              'transition-colors duration-150',
              hasActiveChild
                ? 'text-sidebar-primary'
                : 'text-sidebar-muted group-hover:text-sidebar-foreground'
            )}
          />
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left">{item.label}</span>
            <ChevronDown
              size={14}
              className={cn(
                'shrink-0 transition-transform duration-200',
                hasActiveChild ? 'text-sidebar-primary' : 'text-sidebar-muted group-hover:text-sidebar-foreground',
                isOpen && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
    );

    if (collapsed && depth === 0) {
      return (
        <DropdownMenu key={item.groupKey}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{groupButton}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium z-[9999]">
              {item.label}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            side="right"
            align="start"
            sideOffset={14}
            className="w-48 bg-popover border-border z-[9999] p-1 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-2 py-1.5 text-xs font-bold text-foreground/70 uppercase tracking-widest border-b border-border mb-1">
              {item.label}
            </div>
            {getLeafDescendants(item, parentIsPrivate).map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = pathname === child.path;
              const isChildLocked = child.isPrivate && !unlockedPaths[child.path!];
              
              const handleChildClick = (e: React.MouseEvent) => {
                if (isChildLocked) {
                  e.preventDefault();
                  setPendingNav({ path: child.path!, label: child.label });
                  setIsPinModalOpen(true);
                } else {
                  onNavigate?.();
                }
              };

              return (
                <DropdownMenuItem
                  key={child.path}
                  asChild
                  className="cursor-pointer focus:bg-accent rounded-md"
                >
                  <NavLink
                    to={child.path!}
                    onClick={handleChildClick}
                    className={cn(
                      'flex items-center gap-2 px-2 py-2 w-full outline-none transition-colors text-sm',
                      isChildActive
                        ? 'text-primary font-semibold bg-primary/10'
                        : 'text-foreground/85 hover:text-foreground font-medium',
                    )}
                  >
                    <ChildIcon
                      size={14}
                      className={cn(
                        'shrink-0 transition-colors duration-150',
                        isChildActive ? 'text-primary' : 'text-muted-foreground'
                      )}
                    />
                    <span className="truncate">{child.label}</span>
                  </NavLink>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <div key={item.groupKey}>
        {groupButton}
        {!collapsed && isOpen && item.children && (
          <div className="ml-2.5 mt-1 flex flex-col gap-0.5 border-l border-sidebar-accent pl-2.5 pb-1">
            {item.children.map((child, idx) => (
              <div key={child.groupKey ?? child.path ?? child.externalUrl ?? idx}>
                {renderItem(child, depth + 1, effectiveIsPrivate)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <ScrollArea className="flex-1 min-h-0 sidebar-scrollbar" type="always">
        <nav className="flex flex-col gap-0.5 p-3">
          {NAV_ITEMS.map((item, i) => (
            <div key={item.groupKey ?? item.path ?? item.externalUrl ?? i}>{renderItem(item)}</div>
          ))}
        </nav>
      </ScrollArea>
      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => {
          setIsPinModalOpen(false);
          setPendingNav(null);
        }}
        onSuccess={handlePinSuccess}
        moduleLabel={pendingNav?.label}
      />
    </TooltipProvider>
  );
};

export default SidebarNav;
